import { AxiosProgressEvent } from "axios";
import { api } from "../libs/baseApi";
import {
  Driver,
  ApiListResponse,
  DriverListParams,
  ApiResponse,
  DriverPayload,
  ApiImportData,
} from "../types";

// Example of fetching driver data
export const getDriverData: (
  params?: DriverListParams
) => Promise<ApiListResponse<Driver>> = async (params) => {
  try {
    const response = await api.get("/drivers", {
      params: {
        page: params?.page,
        limit: params?.limit,
        ...params?.filters,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching driver data:", error);
    throw error;
  }
};

// Example of fetching driver data by id
export const getDriverDataById: (id: string) => Promise<Driver> = async (
  id
) => {
  try {
    const response = await api.get(`/drivers/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching driver data by id:", error);
    throw error;
  }
};

// Example of creating driver data
export const createDriverData: (
  data: Partial<DriverPayload>
) => Promise<ApiResponse<Driver>> = async (data) => {
  try {
    const response = await api.post("/drivers", data);
    return response.data;
  } catch (error) {
    console.error("Error creating driver data:", error);
    throw error;
  }
};

// Example of updating driver data
export const updateDriverData: (
  data: Partial<DriverPayload>
) => Promise<ApiResponse<Driver>> = async (data) => {
  try {
    const response = await api.put(`/drivers/${data.ulid}`, data);
    return response.data;
  } catch (error) {
    console.error("Error updating driver data:", error);
    throw error;
  }
};

// Example of deleting driver data
export const deleteDriverData: (
  id: string
) => Promise<ApiResponse<Driver>> = async (id) => {
  try {
    const response = await api.delete(`/drivers/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting driver data:", error);
    throw error;
  }
};

// Example of resetting driver password
export const resetDriverPassword: (
  id: string
) => Promise<ApiResponse<Driver>> = async (id) => {
  try {
    const response = await api.post(`/drivers/reset-password`, {
      driver_ulid: id,
    });
    return response.data;
  } catch (error) {
    console.error("Error resetting driver password:", error);
    throw error;
  }
};

export const exportDriverData: (
  params?: DriverListParams
) => Promise<{ data: any; filename: string }> = async (params) => {
  let filename;
  try {
    const response = await api.get("/drivers/export", {
      params: {
        ...params?.filters,
      },
      responseType: "blob",
    });

    const contentDisposition = response.headers["content-disposition"];

    if (contentDisposition) {
      const filenameMatch = contentDisposition.split("filename=")[1];
      if (filenameMatch) {
        filename = filenameMatch;
      }
    }

    return {
      data: response.data,
      filename,
    };
  } catch (error) {
    console.error("Error exporting driver data:", error);
    throw error;
  }
};

export const importDriverData: ({
  data,
  progressCallback,
}: {
  data: FormData;
  progressCallback: (progressEvent: AxiosProgressEvent) => void;
}) => Promise<ApiResponse<ApiImportData>> = async ({
  data,
  progressCallback,
}) => {
  try {
    const response = await api.post("/drivers/import", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: progressCallback,
    });
    return response.data;
  } catch (error) {
    console.error("Error importing driver data:", error);
    throw error;
  }
};
